import axios from 'axios'

// API base URL - will be proxied to backend in development
export const API_BASE_URL = '/api'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increased to 120 seconds (2 minutes) to prevent timeouts on slow connections
  withCredentials: true // Include cookies for session authentication
})

// Function to get API token from server
async function getApiToken() {
  try {
    // First check if we already have it in localStorage
    const cachedToken = localStorage.getItem('api_token');
    if (cachedToken) {
      return cachedToken;
    }

    // Fetch token from server
    const response = await axios.get('/api/token');
    const token = response.data.data?.token;

    if (token) {
      // Cache the token
      localStorage.setItem('api_token', token);
      return token;
    }

    return null;
  } catch (error) {
    console.warn('Failed to fetch API token:', error.message);
    return null;
  }
}

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Get API token
    const apiToken = await getApiToken();

    if (apiToken) {
      // Use x-api-token header for API authentication
      config.headers['x-api-token'] = apiToken;
    }
    // If no token is configured, the server will allow access in development mode

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    return Promise.reject(error)
  }
)

// API endpoints
export const endpoints = {
  // Verification endpoints
  verify: '/verify',
  upload: '/verify/upload',
  export: '/verify/export',
  stats: '/verify/stats',
  results: '/verify/results',
  checkSettings: '/verify/check-settings',

  // Admin endpoints
  settings: '/admin/settings',
  syncDisposable: '/admin/sync-disposable',
  systemInfo: '/admin/system-info',
  testSmtp: '/admin/test-smtp',
  licenseStatus: '/admin/license-status',
  clearCache: '/admin/clear-cache',
  errorStrings: '/admin/error-strings',
  dummyEmails: '/admin/dummy-emails',
  bulkScanSettings: '/admin/bulk-scan-settings',
  taskRunnerStatus: '/admin/task-runner/status',
  taskRunnerRun: '/admin/task-runner/run',

  // General API endpoints
  webhook: '/webhook',
  push: '/push',
  fetch: '/fetch',
  batch: '/batch',
  health: '/health',
  seo: '/public/seo'
}

// API service functions
export const apiService = {
  // Verification
  verifyEmail: (email) => api.post(endpoints.verify, { email }),
  checkSettings: () => api.get(endpoints.checkSettings),

  uploadCSV: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(endpoints.upload, formData)
  },

  uploadCSVWithProgress: (file, onUploadProgress, signal) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(endpoints.upload, formData, {
      onUploadProgress,
      signal,
    });
  },

  exportResults: (status, format = 'csv') =>
    api.get(endpoints.export, {
      params: { status, format },
      responseType: format === 'csv' ? 'blob' : 'json'
    }),

  getStats: () => api.get(endpoints.stats),

  getResults: (params = {}) => api.get(endpoints.results, { params }),

  deleteOldResults: (days = 30) =>
    api.delete(endpoints.results, { params: { days } }),

  // Admin
  updateSettings: (settings) => api.post(endpoints.settings, settings),
  getSettings: () => api.get(endpoints.settings),

  syncDisposable: () => api.post(endpoints.syncDisposable),

  getSeoMetadata: () => api.get(endpoints.seo),

  getSystemInfo: () => api.get(endpoints.systemInfo),

  testSmtp: (smtpConfig) =>
    api.post(endpoints.testSmtp, smtpConfig),

  testImap: (imapConfig) =>
    api.post('admin/test-imap', imapConfig),

  getLicenseStatus: () => api.get(endpoints.licenseStatus),

  clearCache: () => api.post(endpoints.clearCache),

  // Third-party integrations
  webhook: (data) => api.post(endpoints.webhook, data),

  pushEmails: (data) => api.post(endpoints.push, data),

  fetchResults: (params = {}) => api.get(endpoints.fetch, { params }),

  batchVerification: (data) => api.post(endpoints.batch, data),

  // Health check
  healthCheck: () => api.get(endpoints.health),

  getErrorStrings: () => api.get(endpoints.errorStrings),
  addErrorString: (value) => api.post(endpoints.errorStrings, { value }),
  deleteErrorString: (id) => api.delete(`${endpoints.errorStrings}/${id}`),

  getDummyEmails: () => api.get(endpoints.dummyEmails),
  addDummyEmail: (subject, message) => api.post(endpoints.dummyEmails, { subject, message }),
  updateDummyEmail: (id, subject, message) => api.put(`${endpoints.dummyEmails}/${id}`, { subject, message }),
  deleteDummyEmail: (id) => api.delete(`${endpoints.dummyEmails}/${id}`),

  getBulkScanSettings: () => api.get(endpoints.bulkScanSettings),
  updateBulkScanSettings: (settings) => api.post(endpoints.bulkScanSettings, settings),
  getTaskRunnerStatus: () => api.get(endpoints.taskRunnerStatus),
  runTaskRunner: () => api.post(endpoints.taskRunnerRun),

  inboxChecker: (email) => api.post('/verify/inbox-checker', { email }),

  // Lead Management
  getLeads: (params = {}) => api.get('/verify/leads', { params }),
  getLeadsTags: () => api.get('/verify/leads/tags'),
  getGroups: () => api.get('/verify/groups'),
  createLead: (data) => api.post('/admin/leads', data),
  updateLead: (id, data) => api.put(`/admin/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/admin/leads/${id}`),

  // Worker concurrency/background settings
  getWorkerSettings: () => api.get('/admin/worker-settings'),
  updateWorkerSettings: (settings) => api.post('/admin/worker-settings', settings),

  // Get worker concurrency setting
  getWorkerConcurrency: () => api.get('/admin/worker-settings').then(response => {
    const settings = response.data.data || {};
    return settings.worker_concurrency || 2;
  }),

  // Job summaries for Results page
  getJobs: (params = {}) => api.get('/verify/jobs', { params }),
  getJobItems: (jobId, params = {}) => api.get(`/verify/jobs/${jobId}/items`, { params }),
  getJobTags: (jobId) => api.get(`/verify/jobs/${jobId}/tags`),

  // Job control
  pauseJob: (jobId) => api.post(`/verify/jobs/${jobId}/pause`),
  resumeJob: (jobId) => api.post(`/verify/jobs/${jobId}/resume`),
  startJob: (jobId) => api.post(`/verify/jobs/${jobId}/start`),
  deleteJob: (jobId) => api.delete(`/verify/jobs/${jobId}`),
  cancelJob: (jobId) => api.post(`/verify/jobs/${jobId}/cancel`),
  retryUnknowns: (jobId) => api.post(`/verify/jobs/${jobId}/retry-unknowns`),


  getProcessedCounts: () => api.get('/verify/processed-counts'),

  // Admin repair functions
  repairJobCounts: () => api.post('/admin/repair-counts-now'),

  // Satellite Nodes
  getSatellites: () => api.get('/admin/satellites'),
  createSatellite: (data) => api.post('/admin/satellites', data),
  deleteSatellite: (id) => api.delete(`/admin/satellites/${id}`),
  resetSatelliteKey: (id) => api.post(`/admin/satellites/${id}/reset-key`),
  updateSatelliteLimit: (id, limit) => api.put(`/admin/satellites/${id}/limit`, { daily_limit: limit }),
  togglePauseSatellite: (id, isPaused) => api.put(`/admin/satellites/${id}/toggle-pause`, { is_paused: isPaused }),
  updateSatelliteConfig: (id, config) => api.put(`/admin/satellites/${id}/config`, config),
  refreshSatelliteDNS: (id) => api.post(`/admin/satellites/${id}/refresh-dns`),
  checkSatelliteBlacklist: (id) => api.post(`/admin/satellites/${id}/check-blacklist`),
  resetSatelliteReliability: (id) => api.post(`/admin/satellites/${id}/reset-reliability`),
  checkPort25: (ip_address) => api.post('/admin/satellites/check-port25', { ip_address }),
  triggerSatellitePort25: (id) => api.post(`/admin/satellites/${id}/trigger-port25`),
}

export default api